import React from "react";
import { MapPin, Check } from "lucide-react";
import { CreateBusinessPayload } from "../../../../../interfaces/BusinessObject";
import CollaboratorInput from "./CollaboratorInput";
import OwnerSelector from "./OwnerSelector";
import { ResolvedUser } from "../../../../shared/UserByPhoneInput/UserByPhoneInput";
import "./styles/BusinessForm.css";

export interface ListingTypeOption {
  id: string;
  label: string;
}

interface BusinessFormProps {
  form: CreateBusinessPayload;
  listingTypeOptions: ListingTypeOption[];
  listingTypeLocked: boolean;
  onChange: (patch: Partial<CreateBusinessPayload>) => void;
  /** Logged-in user's id — drives the "Me" option in the owner selector. */
  currentUserId: string;
  /** Pre-fills the "Someone else" chip when editing a business with an external owner. */
  initialResolvedOwner?: ResolvedUser | null;
  /** When false the owner selector is hidden (caller can't change ownership). */
  showOwnerSelector?: boolean;
  /** When false the collaborators input is hidden (e.g. onboarding drafts). */
  showCollaborators?: boolean;
  /** Opens the map-pin picker. The modal owns the picker overlay. */
  onPickLocation: () => void;
}

const BusinessForm: React.FC<BusinessFormProps> = ({
  form,
  listingTypeOptions,
  listingTypeLocked,
  onChange,
  currentUserId,
  initialResolvedOwner,
  showOwnerSelector = true,
  showCollaborators = true,
  onPickLocation,
}) => {
  const hasLocation =
    typeof form.latitude === "number" && typeof form.longitude === "number";

  return (
    <div className="biz-form">
      {/* Business Name */}
      <div className="biz-field">
        <label className="biz-label">
          Business Name <span className="biz-required">*</span>
        </label>
        <input
          type="text"
          className="biz-input"
          placeholder="e.g. Coffee Corner"
          value={form.name}
          maxLength={80}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      {/* Listing Type */}
      <div className="biz-field">
        <label className="biz-label">
          Listing Type <span className="biz-required">*</span>
        </label>
        <div className={`biz-type-grid ${listingTypeLocked ? "biz-type-locked" : ""}`}>
          {listingTypeOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`biz-type-chip ${form.listingType === option.id ? "active" : ""}`}
              onClick={() => !listingTypeLocked && onChange({ listingType: option.id })}
              disabled={listingTypeLocked}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listingTypeLocked && (
          <p className="biz-locked-hint">Listing type cannot be changed after creation.</p>
        )}
      </div>

      {/* Business Phone */}
      <div className="biz-field">
        <label className="biz-label">
          Business Phone <span className="biz-required">*</span>
        </label>
        <input
          type="tel"
          className="biz-input"
          placeholder="10-digit phone number"
          value={form.phone}
          maxLength={10}
          onChange={(e) => onChange({ phone: e.target.value.replace(/\D/g, "") })}
        />
      </div>

      {/* Description */}
      <div className="biz-field">
        <label className="biz-label">Description</label>
        <textarea
          className="biz-textarea"
          placeholder="Tell customers what you offer..."
          value={form.description ?? ""}
          maxLength={300}
          rows={3}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </div>

      {/* Location */}
      <div className="biz-field">
        <label className="biz-label">Location</label>
        <p className="biz-sublabel">
          Drop a pin so customers can find you on the map.
        </p>
        <button
          type="button"
          className={`biz-location-btn ${hasLocation ? "is-set" : ""}`}
          onClick={onPickLocation}
        >
          <span className="biz-location-btn__icon">
            {hasLocation ? <Check size={18} /> : <MapPin size={18} />}
          </span>
          <span className="biz-location-btn__text">
            {hasLocation ? (
              <>
                <span className="biz-location-btn__title">Location set</span>
                <span className="biz-location-btn__coords">
                  {form.latitude!.toFixed(5)}, {form.longitude!.toFixed(5)}
                </span>
              </>
            ) : (
              <span className="biz-location-btn__title">Select location</span>
            )}
          </span>
          {hasLocation && (
            <span className="biz-location-btn__edit">Change</span>
          )}
        </button>
      </div>

      {/* Owner */}
      {showOwnerSelector && (
        <div className="biz-field">
          <label className="biz-label">Owner</label>
          <p className="biz-sublabel">
            Choose who controls this business. You can transfer ownership later.
          </p>
          <OwnerSelector
            currentUserId={currentUserId}
            value={form.ownerId}
            initialResolvedOwner={initialResolvedOwner}
            onChange={(ownerId) => onChange({ ownerId })}
          />
        </div>
      )}

      {/* Collaborators */}
      {showCollaborators && (
        <div className="biz-field">
          <label className="biz-label">Collaborators</label>
          <p className="biz-sublabel">
            Add phone numbers of people who can manage this business's listings.
          </p>
          <CollaboratorInput
            value={form.collaborators}
            onChange={(collaborators) => onChange({ collaborators })}
          />
        </div>
      )}
    </div>
  );
};

export default BusinessForm;
